import { useState } from "react";
import { Box, Button, Paper, Tab, Tabs, Typography } from "@mui/material";
import { List } from "@refinedev/mui";
import { useForm } from "react-hook-form";
import { useTranslate } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { FinancialFilters } from "@modules/financial/components/FinancialFilters";
import { RebuildDialog } from "@modules/financial/components/RebuildDialog";
import { financialQueryKey } from "@modules/financial/services";
import { FinancialFilterValues, FinancialTab } from "@modules/financial/types";

import { FinancialTable } from "./FinancialTable";
import { VolumetryEmailPanel } from "./VolumetryEmailPanel";

const TABS: FinancialTab[] = [
  "clients_by_plan",
  "client_plans",
  "clients_by_category",
  "plans_by_category",
  "media",
  "volumetry_email",
];

function defaultFilters(): FinancialFilterValues {
  return {
    integration: "",
    agreement_id: null,
    cpf: "",
    user_id: "",
    product_id: null,
    plan_slug_integration: "",
    level_id: null,
    year: String(new Date().getFullYear()),
    month: "",
    media_type: "",
    status: "",
    tipo: "",
    situacao: "",
    modelo: "",
  };
}

export function FinancialPage() {
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const initialFilters = defaultFilters();
  const [applied, setApplied] = useState<FinancialFilterValues>(initialFilters);
  const methods = useForm<FinancialFilterValues>({ defaultValues: initialFilters });

  const tabParam = searchParams.get("tab") as FinancialTab | null;
  const tab: FinancialTab = tabParam && TABS.includes(tabParam) ? tabParam : "clients_by_plan";
  const isVolumetry = tab === "volumetry_email";
  const hint =
    tab === "media"
      ? translate("financial_admin.messages.media_duplicate")
      : tab === "plans_by_category"
        ? translate("financial_admin.messages.plans_by_category_hint")
        : "";

  const handleApply = (values: FinancialFilterValues) => {
    if (values.month && !values.year) {
      methods.setError("month", { message: translate("financial_admin.messages.month_requires_year") });
      return;
    }
    methods.clearErrors("month");
    setApplied(values);
  };

  return (
    <List
      title={translate("financial_admin.financial_admin")}
      headerButtons={
        isVolumetry ? (
          <span />
        ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => queryClient.invalidateQueries({ queryKey: [financialQueryKey] })}
            >
              {translate("financial_admin.actions.refresh")}
            </Button>
            <Button variant="contained" onClick={() => setRebuildOpen(true)}>
              {translate("financial_admin.actions.rebuild")}
            </Button>
          </Box>
        )
      }
    >
      <Tabs
        value={tab}
        onChange={(_, value: FinancialTab) => {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", value);
            return next;
          });
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          minHeight: 48,
          bgcolor: "grey.100",
          borderRadius: 2,
          p: 0.5,
          "& .MuiTabs-indicator": { display: "none" },
          "& .MuiTabs-flexContainer": { gap: 0.5 },
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            minHeight: 40,
            borderRadius: 1.5,
            color: "text.secondary",
            "&.Mui-selected": {
              color: "primary.main",
              bgcolor: "background.paper",
            },
          },
        }}
      >
        {TABS.map((value) => (
          <Tab key={value} value={value} label={translate(`financial_admin.tabs.${value}`)} />
        ))}
      </Tabs>

      {isVolumetry ? (
        <VolumetryEmailPanel />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ p: 2, bgcolor: "grey.50", borderBottom: 1, borderColor: "divider" }}>
            <FinancialFilters tab={tab} methods={methods} onApply={handleApply} />
            {hint && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, maxWidth: 760 }}>
                {hint}
              </Typography>
            )}
          </Box>
          <FinancialTable tab={tab} filters={applied} />
        </Paper>
      )}
      <RebuildDialog open={rebuildOpen} onClose={() => setRebuildOpen(false)} />
    </List>
  );
}
